import { ExportBulkSpecialtySummaryByZipRequestModel, ExportBulkZipClassAggregateResultsRequestModel, ExportNextClosestProviderActionEnum, ExportPciCrossProjectRequestModel, VolumeServicingProvidersRequestModel, VolumeAdequacySpecialtySummariesRequestModel, VolumeZipSummariesRequestModel, ExportAdequacyVolumeAggregateRequestModel, ExportCCIIOProviderModel, ExportCCIIODataValidationsModel, ProviderSetVersionSelection } from "@app/exports/exportModels";
import { NotFound } from "@app/shared/ErrorPages";
import React, { JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, RouteComponentProps, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ExportFormatType, AdequacyAggregateType, AdequacyAggregateGranularity, } from "@app/exports/enumerations";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { compact } from "lodash";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import ExportService from "@app/exports/exportService";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { VolumeReportsWizardContextSteps, ReportFormatOptionsType, reportFormatOptions, CountiesFilterRadioOptionsType, countiesFilterRadioOptions } from "@app/reports/volume-reports/import/interfaces";
import { VolumeReportType, VolumeReportsProvider, VolumeReportsContext } from "@app/reports/volume-reports/import/VolumeReportsContext";
import VolumeReportsConfig from "@app/reports/volume-reports/steps/VolumeReportsConfig";
import VolumeReportsFilters from "@app/reports/volume-reports/steps/VolumeReportsFilters";
import VolumeReportsSelectProjects from "@app/reports/volume-reports/steps/VolumeReportsSelectProjects";
import VolumeReportsSelectServiceArea from "@app/reports/volume-reports/steps/VolumeReportsSelectServiceArea";
import VolumeReportsSelectSpecialties from "@app/reports/volume-reports/steps/VolumeReportsSelectSpecialties";
import useIsNPMDashboardActive from "@app/network-portfolio-management/hooks/useIsNPMDashboardActive";
import { isSpecialtySelectionValid } from "@app/reports/volume-reports/import/specialtySelection";

const VolumeReportsWizard = (props: RouteComponentProps): JSX.Element => {
  const { reportType } = useParams<{ reportType: VolumeReportType }>();
  const user = useContext(UserContext);

  if (!["providers", "zip-providers", "specialty", "pci", "specialty-summary-zip-code", "zip-summary", "zip-class-aggregate", "countyaggregate", "stateaggregate", "specialtyaggregate", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(reportType) || (!["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(reportType) && !user.roles.includes(Roles.VOLUME_REPORTER))) {
    return <NotFound />;
  }
  return (
    <VolumeReportsProvider key={reportType} reportType={reportType}>
      <VolumeReportsWizardContent {...props} />
    </VolumeReportsProvider>
  );
};

const VolumeReportsWizardContent = (props: RouteComponentProps): JSX.Element => {
  const wizardContext = useContext(VolumeReportsContext);
  const user = useContext(UserContext);
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const [pageIsValid, setPageIsValid] = useState(false);
  const furthestPage = useRef(wizardContext.reportType !== "specialtyaggregate" ? VolumeReportsWizardContextSteps.SelectServiceArea : VolumeReportsWizardContextSteps.SelectSpecialty);
  const exportService = useRef(new ExportService()).current;
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const isNPMDashboardActive = useIsNPMDashboardActive();

  useEffect(() => {
    if (!wizardContext.loading) {
      focusFirstInteractiveElement(wizardBodyRef);
    }
  }, [wizardContext.page, wizardContext.loading]);

  const hasDashboardFilters = useMemo(() => {
    return Object.values(wizardContext.dashboardFilters).length > 0;
  }, [wizardContext.dashboardFilters]);

  const isNotZipReport = wizardContext.reportType?.toString() !== "zip-summary" && wizardContext.reportType?.toString() !== "zip-class-aggregate";
  // Both specialty filter axes (Physician/Facility and Required/Informational) must retain at least
  // one member, otherwise the report can only ever return no data. Guard the wizard buttons directly
  // so this cannot be bypassed by jumping between steps via the step header.
  const specialtySelectionValid = isSpecialtySelectionValid({
    includeFacilitySpecialties: wizardContext.includeFacilitySpecialties,
    includeInformationalSpecialties: isNotZipReport ? wizardContext.includeInformationalSpecialties : true,
    includePhysicianSpecialties: wizardContext.includePhysicianSpecialties,
    includeRequiredSpecialties: isNotZipReport ? wizardContext.includeRequiredSpecialties : true,
  });

  const pageTitle = useRef(() => {
    switch (wizardContext.reportType) {
      case "providers":
        return "Providers Report";
      case "zip-providers":
        return "Providers by Zip Code Report";
      case "specialty":
        return "Specialty Summary by County Report";
      case "countyaggregate":
        return "County Aggregate Report";
      case "stateaggregate":
        return "State Aggregate Report";
      case "specialtyaggregate":
        return "Specialty Aggregate Report";
      case "zip-summary":
        return "Zip Code Summary Report";
      case "specialty-summary-zip-code":
        return "Specialty Summary by Zip Code Report";
      case "zip-class-aggregate":
        return "Summary by Zip Classification Report";
      case "pci":
        return "PCI Cross-Project Report";
      case "cciio":
        return "CCIIO QHP Download"
      case "cciioDataValidationsSummary":
        return "CCIIO Data Validation Summary"
      case "cciioDataValidations":
        return "CCIIO Data Validation"
    }
  }).current();

  const handleUpdatePage = (newPage: number) => {
    if (["countyaggregate", "stateaggregate"].includes(wizardContext.reportType)) {
      if (newPage > 0) {
        newPage++;
      }
    }
    else if (["specialtyaggregate"].includes(wizardContext.reportType)) {
      newPage++;
    }

    const step = Object.entries(VolumeReportsWizardContextSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as VolumeReportsWizardContextSteps;
    wizardContext.setPage(VolumeReportsWizardContextSteps[step] as unknown as VolumeReportsWizardContextSteps);
    wizardContext.setShowChangeWarning(true);
  };

  const getCurrentStep = () => {
    if (!["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType)) {
      if (["countyaggregate", "stateaggregate"].includes(wizardContext.reportType)) {
        switch (wizardContext.page) {
          case VolumeReportsWizardContextSteps.SelectServiceArea:
            return 0;
          case VolumeReportsWizardContextSteps.SelectProjects:
            return 1;
          case VolumeReportsWizardContextSteps.Filters:
            return 2;
          case VolumeReportsWizardContextSteps.Configure:
            return 3;
          default:
            return;
        }
      }
      else if (["specialtyaggregate"].includes(wizardContext.reportType)) {
        switch (wizardContext.page) {
          case VolumeReportsWizardContextSteps.SelectSpecialty:
            return 0;
          case VolumeReportsWizardContextSteps.SelectProjects:
            return 1;
          case VolumeReportsWizardContextSteps.Filters:
            return 2;
          case VolumeReportsWizardContextSteps.Configure:
            return 3;
          default:
            return;
        }
      }
      else {
        return wizardContext.page;
      }
    } else if (["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType)) {
      switch (wizardContext.page) {
        case VolumeReportsWizardContextSteps.SelectProjects:
          return 0;
        case VolumeReportsWizardContextSteps.Configure:
          return 1;
        default:
          return;
      }
    }
  }

  const getHumanReadablePageNames = () => {
    const humanReadablePageNames = {
      [VolumeReportsWizardContextSteps.SelectServiceArea]: !["pci", "cciio", "specialtyaggregate", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) ? "Service Area" : null,
      [VolumeReportsWizardContextSteps.SelectSpecialty]: !["cciio", "cciioDataValidations", "cciioDataValidationsSummary", "countyaggregate", "pci", "stateaggregate"].includes(wizardContext.reportType) ? "Specialties" : null,
      [VolumeReportsWizardContextSteps.SelectProjects]: "Projects",
      [VolumeReportsWizardContextSteps.Filters]: !["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) ? "Filters" : null,
      [VolumeReportsWizardContextSteps.Configure]: "Configure",
    };

    return compact(Object.values(humanReadablePageNames));
  }

  const goToPreviousPage = () => {
    switch (wizardContext.page) {
      case VolumeReportsWizardContextSteps.SelectSpecialty:
        if (wizardContext.reportType !== "specialtyaggregate") {
          wizardContext.setPage(VolumeReportsWizardContextSteps.SelectServiceArea);
          wizardContext.setShowChangeWarning(true);
        }
        break;
      case VolumeReportsWizardContextSteps.SelectProjects:
        if (wizardContext.reportType !== "countyaggregate" && wizardContext.reportType !== "stateaggregate") {
          wizardContext.setPage(VolumeReportsWizardContextSteps.SelectSpecialty);
          wizardContext.setShowChangeWarning(true);
        }
        else {
          wizardContext.setPage(VolumeReportsWizardContextSteps.SelectServiceArea);
          wizardContext.setShowChangeWarning(true);
        }
        break;
      case VolumeReportsWizardContextSteps.Filters:
        wizardContext.setPage(VolumeReportsWizardContextSteps.SelectProjects);
        wizardContext.setShowChangeWarning(true);
        break;
      case VolumeReportsWizardContextSteps.Configure:
        if (!["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType)) {
          wizardContext.setPage(VolumeReportsWizardContextSteps.Filters);
        } else {
          wizardContext.setPage(VolumeReportsWizardContextSteps.SelectProjects);
        }
        break;
    }
  };

  const validateAndAdvance = () => {
    let newPage: VolumeReportsWizardContextSteps;
    switch (wizardContext.page) {
      case VolumeReportsWizardContextSteps.SelectServiceArea:
        if (wizardContext.reportType !== "countyaggregate" && wizardContext.reportType !== "stateaggregate") {
          newPage = VolumeReportsWizardContextSteps.SelectSpecialty;
        }
        else {
          newPage = VolumeReportsWizardContextSteps.SelectProjects
        }
        break;
      case VolumeReportsWizardContextSteps.SelectSpecialty:
        newPage = VolumeReportsWizardContextSteps.SelectProjects;
        break;
      case VolumeReportsWizardContextSteps.SelectProjects:
        if (wizardContext.projects.length > 0) {
          if (!["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType)) {
            newPage = VolumeReportsWizardContextSteps.Filters;
          } else {
            newPage = VolumeReportsWizardContextSteps.Configure;
          }
        }
        break;
      case VolumeReportsWizardContextSteps.Filters:
        newPage = VolumeReportsWizardContextSteps.Configure;
        break;
    }
    if (newPage) {
      if (newPage >= furthestPage.current) {
        // we don't reset configure
        furthestPage.current = Math.min(newPage, VolumeReportsWizardContextSteps.Filters);
        wizardContext.setShowChangeWarning(false);
      }
      wizardContext.setPage(newPage);
    }
  };

  const showNoProjectsAlert = useMemo(() => {
    return wizardContext.page === VolumeReportsWizardContextSteps.SelectProjects &&
      !wizardContext.loading &&
      wizardContext.projects.length === 0;
  }, [wizardContext.projects, wizardContext.page, wizardContext.loading]);

  const currentWizardStep = useMemo(() => {
    switch (wizardContext.page) {
      case VolumeReportsWizardContextSteps.SelectServiceArea:
        return <VolumeReportsSelectServiceArea pageIsValid={setPageIsValid} />;
      case VolumeReportsWizardContextSteps.SelectSpecialty:
        return <VolumeReportsSelectSpecialties pageIsValid={setPageIsValid} />;
      case VolumeReportsWizardContextSteps.SelectProjects:
        return <VolumeReportsSelectProjects pageIsValid={setPageIsValid} />;
      case VolumeReportsWizardContextSteps.Filters:
        return <VolumeReportsFilters {...props} pageIsValid={setPageIsValid} isStandardizedReport={true} />;
      case VolumeReportsWizardContextSteps.Configure:
        return <VolumeReportsConfig pageIsValid={setPageIsValid} {...props} />;
    }
  }, [wizardContext.page]);

  const getExportType = (format: ReportFormatOptionsType) => {
    switch (format) {
      case reportFormatOptions[0]:
        return ExportFormatType.CSV
      case reportFormatOptions[1]:
        return ExportFormatType.Pipe
      case reportFormatOptions[2]:
        return ExportFormatType.Tab
    }
  };

  const getNetworkAnalysisTypes = (types: CountiesFilterRadioOptionsType[]) => {
    const networkAnalysisTypes: NetworkAssessmentAnalysisType[] = [];
    types.forEach((type) => networkAnalysisTypes.push(getNetworkAnalysisType(type)));
    return networkAnalysisTypes;
  };

  const getNetworkAnalysisType = (type: CountiesFilterRadioOptionsType) => {
    switch (type) {
      case countiesFilterRadioOptions[0]:
        return NetworkAssessmentAnalysisType.ServiceArea
      case countiesFilterRadioOptions[1]:
        return NetworkAssessmentAnalysisType.Opportunity
    }
  };

  const isHsdFormat = (filter: string) => filter !== "Default Format" && filter !== 'CCIIO PPF Report' && filter !== 'Distinct Servicing Providers';

  const handleGenerate = async () => {
    const columnPresetId = featureFlags.has(Features.ADEQUACY_REPORT_CUSTOMIZATION) && wizardContext.selectedColumnPreset.id > 0 ? wizardContext.selectedColumnPreset.id : null;
    switch (wizardContext.reportType) {
      case "providers":
      case "zip-providers":
        const isNcp = wizardContext.providerType.toString() === ProviderNetworkTypeEnum.NextClosestProviders.toString();
        const providersRequestModel: VolumeServicingProvidersRequestModel = {
          allowParallelization: true,
          appendPatternOfCareProviders: wizardContext.appendPatternOfCareProviders && user.roles.includes(Roles.PATTERN_OF_CARE_USER),
          cargoColumnsToInclude: undefined,
          customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          generateNoDataFoundReadme: true,
          hsdColumns: undefined,
          hsdFormat: isHsdFormat(wizardContext.reportFilter),
          impactProviderType: wizardContext.impactProviderType,
          IncludeActiveRegionSets: wizardContext.activeCounties,
          IncludeApprovedRegionSets: wizardContext.approvedCounties,
          includeCargoColumns: wizardContext.includeCargoColumns,
          IncludeFacilitySpecialties: wizardContext.includeFacilitySpecialties,
          IncludeInformationalSpecialties: wizardContext.includeInformationalSpecialties,
          IncludePendingRegionSets: wizardContext.pendingCounties,
          IncludePhysicianSpecialties: wizardContext.includePhysicianSpecialties,
          IncludeRequiredSpecialties: wizardContext.includeRequiredSpecialties,
          isCCIIOPPFReport: wizardContext.reportFilter === 'CCIIO PPF Report',
          isDistinctServicingProviderReport: wizardContext.reportFilter === 'Distinct Servicing Providers',
          isZipAnalysis: wizardContext.reportType === "zip-providers",
          membershipType: featureFlags.has(Features.VOLUME_PROVIDER_REPORT_MEMBERSHIP_FILTER) ? wizardContext.membershipTypeFilter : undefined,
          networkAssessmentAnalysisTypes: [getNetworkAnalysisType(wizardContext.servicingType)],
          networkProviderFilterType: wizardContext.networkProviderFilterType,
          nextClosestProviderAction: isNcp ? ExportNextClosestProviderActionEnum.FilterToNextClosestProviderOnly : wizardContext.appendNcp ? ExportNextClosestProviderActionEnum.AppendNextClosestProviders : ExportNextClosestProviderActionEnum.None,
          providerNetworkTypes: isNcp ? [ProviderNetworkTypeEnum.Network] : [wizardContext.providerType],
          publishedOnly: wizardContext.publishedOnly,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          regionSetIds: wizardContext.allServiceAreas ? [] : wizardContext.serviceAreas.flatMap(s => s.childServiceAreas.filter((sp) => sp.isChecked).map(c => c.serviceAreaValue)),
          reportCustomizationId: columnPresetId,
          runAsynchronous: true,
          separateReportOutput: wizardContext.splitReport,
          sharedUsers: wizardContext.sharedUsers,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          specialtyGroupCodes: wizardContext.allSpecialties ? [] : wizardContext.specialties.filter((sp) => sp.isChecked).map((sp) => sp.specialtyGroupCode),
          suppressHSDColumns: wizardContext.removeNonHSDColumns,
        };
        await exportService.postVolumeServicingProviders(providersRequestModel);
        break;
      case "specialty":
        // aka summariesRequest
        const volumeSpecialtySummariesRequestModel: VolumeAdequacySpecialtySummariesRequestModel = {
          allowParallelization: true,
          customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
          eMailOnSuccess: wizardContext.alertWhenReady,
          excludeZeroMembership: wizardContext.excludeZeroMembership,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          generateNoDataFoundReadme: true,
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includeFacilitySpecialties: wizardContext.includeFacilitySpecialties,
          includeInformationalSpecialties: wizardContext.includeInformationalSpecialties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includePhysicianSpecialties: wizardContext.includePhysicianSpecialties,
          includeRequiredSpecialties: wizardContext.includeRequiredSpecialties,
          isVolumeReport: true,
          membershipType: wizardContext.membershipTypeFilter,
          networkAssessmentAnalysisTypes: getNetworkAnalysisTypes(wizardContext.specialtySummaryServicingTypes),
          publishedOnly: wizardContext.publishedOnly,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          regionSetIds: wizardContext.allServiceAreas ? [] : wizardContext.serviceAreas.flatMap(s => s.childServiceAreas.filter((sp) => sp.isChecked).map(c => c.serviceAreaValue)),
          reportCustomizationId: columnPresetId,
          runAsynchronous: true,
          separateReportOutput: wizardContext.splitReport,
          sharedUsers: wizardContext.sharedUsers,
          showExceptionComment: undefined,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          specialtyGroupCodes: wizardContext.allSpecialties ? [] : wizardContext.specialties.filter((sp) => sp.isChecked).map((sp) => sp.specialtyGroupCode),
        };
        await exportService.postVolumeSpecialtySummaries(volumeSpecialtySummariesRequestModel);
        break;
      case "zip-summary":
        const zipSummariesRequestModel: VolumeZipSummariesRequestModel = {
          allowParallelization: true,
          customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          generateNoDataFoundReadme: true,
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includeFacilitySpecialties: wizardContext.includeFacilitySpecialties,
          includeInformationalSpecialties: wizardContext.includeInformationalSpecialties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includePhysicianSpecialties: wizardContext.includePhysicianSpecialties,
          includeRequiredSpecialties: wizardContext.includeRequiredSpecialties,
          networkAssessmentAnalysisType: getNetworkAnalysisType(wizardContext.servicingType),
          publishedOnly: wizardContext.publishedOnly,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          regionSetIds: wizardContext.allServiceAreas ? [] : wizardContext.serviceAreas.flatMap(s => s.childServiceAreas.filter((sp) => sp.isChecked).map(c => c.serviceAreaValue)),
          reportCustomizationId: columnPresetId,
          runAsynchronous: true,
          separateReportOutput: wizardContext.splitReport,
          sharedUsers: wizardContext.sharedUsers,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          specialtyGroupCodes: wizardContext.allSpecialties ? [] : wizardContext.specialties.filter((sp) => sp.isChecked).map((sp) => sp.specialtyGroupCode),
        }
        await exportService.postVolumeZipSummaries(zipSummariesRequestModel);

        break;
      case "stateaggregate":
        const stateVolumeAggregateRequestModel: ExportAdequacyVolumeAggregateRequestModel =
        {
          aggregateGranularity: AdequacyAggregateGranularity.State,
          aggregateType: AdequacyAggregateType.RegionSet,
          allowParallelization: true,
          customProjectIds: wizardContext.projects
            .filter((sp) => sp.isChecked)
            .map((project) => project.customProjectId),
          generateNoDataFoundReadme: true,
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includeFacilitySpecialties:
            wizardContext.includeFacilitySpecialties,
          includeInformationalSpecialties:
            wizardContext.includeInformationalSpecialties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includePhysicianSpecialties:
            wizardContext.includePhysicianSpecialties,
          includeRequiredSpecialties:
            wizardContext.includeRequiredSpecialties,
          isVolumeReport: true,
          networkAssessmentAnalysisTypes: [getNetworkAnalysisType(
            wizardContext.servicingType,
          )],
          publishedOnly: wizardContext.publishedOnly,
          regionSetGapFilter: wizardContext.statesGapsFilter,
          regionSetIds: wizardContext.allServiceAreas
            ? []
            : wizardContext.serviceAreas.flatMap((s) =>
              s.childServiceAreas
                .filter((sp) => sp.isChecked)
                .map((c) => c.serviceAreaValue),
            ),
          separateReportOutput: wizardContext.splitReport,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          specialtyGroupCodes: wizardContext.allSpecialties
            ? []
            : wizardContext.specialties
              .filter((sp) => sp.isChecked)
              .map((sp) => sp.specialtyGroupCode),

          //export base model props
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          reportCustomizationId: columnPresetId,
          runAsynchronous: true,
          sharedUsers: wizardContext.sharedUsers,
        };
        await exportService.postVolumeAdequacyAggregates(
          stateVolumeAggregateRequestModel,
        );
        break;
      case "countyaggregate":
        const volumeAggregateRequestModel: ExportAdequacyVolumeAggregateRequestModel =
        {
          aggregateGranularity: AdequacyAggregateGranularity.County,
          aggregateType: AdequacyAggregateType.RegionSet,
          allowParallelization: true,
          customProjectIds: wizardContext.projects
            .filter((sp) => sp.isChecked)
            .map((project) => project.customProjectId),
          generateNoDataFoundReadme: true,
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includeFacilitySpecialties:
            wizardContext.includeFacilitySpecialties,
          includeInformationalSpecialties:
            wizardContext.includeInformationalSpecialties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includePhysicianSpecialties:
            wizardContext.includePhysicianSpecialties,
          includeRequiredSpecialties:
            wizardContext.includeRequiredSpecialties,
          isVolumeReport: true,
          networkAssessmentAnalysisTypes: [getNetworkAnalysisType(
            wizardContext.servicingType,
          )],
          publishedOnly: wizardContext.publishedOnly,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          regionSetIds: wizardContext.allServiceAreas
            ? []
            : wizardContext.serviceAreas.flatMap((s) =>
              s.childServiceAreas
                .filter((sp) => sp.isChecked)
                .map((c) => c.serviceAreaValue),
            ),
          separateReportOutput: wizardContext.splitReport,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          specialtyGroupCodes: wizardContext.allSpecialties
            ? []
            : wizardContext.specialties
              .filter((sp) => sp.isChecked)
              .map((sp) => sp.specialtyGroupCode),

          //export base model props
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          reportCustomizationId: columnPresetId,
          runAsynchronous: true,
          sharedUsers: wizardContext.sharedUsers,
        };
        await exportService.postVolumeAdequacyAggregates(
          volumeAggregateRequestModel,
        );
        break;
      case "specialtyaggregate":
        const specialtyVolumeAggregateRequestModel: ExportAdequacyVolumeAggregateRequestModel =
        {
          aggregateGranularity: AdequacyAggregateGranularity.ServiceArea,
          aggregateType: AdequacyAggregateType.SpecialtyGroup,
          allowParallelization: true,
          customProjectIds: wizardContext.projects
            .filter((sp) => sp.isChecked)
            .map((project) => project.customProjectId),
          generateNoDataFoundReadme: true,
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includeFacilitySpecialties:
            wizardContext.includeFacilitySpecialties,
          includeInformationalSpecialties:
            wizardContext.includeInformationalSpecialties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includePhysicianSpecialties:
            wizardContext.includePhysicianSpecialties,
          includeRequiredSpecialties:
            wizardContext.includeRequiredSpecialties,
          isVolumeReport: true,
          networkAssessmentAnalysisTypes: [getNetworkAnalysisType(
            wizardContext.servicingType,
          )],
          publishedOnly: wizardContext.publishedOnly,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          regionSetIds: [],
          separateReportOutput: wizardContext.splitReport,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          specialtyGroupCodes: wizardContext.allSpecialties
            ? []
            : wizardContext.specialties
              .filter((sp) => sp.isChecked)
              .map((sp) => sp.specialtyGroupCode),

          //export base model props
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          reportCustomizationId: columnPresetId,
          runAsynchronous: true,
          sharedUsers: wizardContext.sharedUsers,
        };
        await exportService.postVolumeAdequacyAggregates(
          specialtyVolumeAggregateRequestModel,
        );
        break;
      case "pci":
        const exportPciCrossProjectRequestModel: ExportPciCrossProjectRequestModel = {
          customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          runAsynchronous: true,
          separateReportOutput: wizardContext.splitReport,
          sharedUsers: wizardContext.sharedUsers,
        };

        await exportService.postExportPciCrossProject(exportPciCrossProjectRequestModel);
        break;
      case "cciio":
      case "cciioDataValidationsSummary":
      case "cciioDataValidations":
        const cciioProviderSetVersionSelections: ProviderSetVersionSelection[] = [
          ...new Map(
            wizardContext.projects
              .filter((p) => p.isChecked)
              .map((p) => [p.networkId, { providerSetId: p.networkId, providerSetVersionId: p.networkProviderSetVersionId }])
          ).values()
        ];
        if (wizardContext.reportType === "cciio") {
          const exportCciioProviderModel: ExportCCIIOProviderModel = {
            eMailOnSuccess: wizardContext.alertWhenReady,
            exportName: wizardContext.reportName,
            formatType: getExportType(wizardContext.reportFormat),
            includeInvalidCCIIORecords: wizardContext.includeInvalidCCIIORecords,
            providerSetVersionSelections: cciioProviderSetVersionSelections,
            runAsynchronous: true,
            sharedUsers: wizardContext.sharedUsers,
          }
          await exportService.postExportCCIIOProviders(exportCciioProviderModel);
        } else if (wizardContext.reportType === "cciioDataValidationsSummary") {
          const exportCciioDataValidationsSummaryModel: ExportCCIIODataValidationsModel = {
            eMailOnSuccess: wizardContext.alertWhenReady,
            exportName: wizardContext.reportName,
            formatType: getExportType(wizardContext.reportFormat),
            providerSetVersionSelections: cciioProviderSetVersionSelections,
            runAsynchronous: true,
            separateReportOutput: wizardContext.splitReport,
            sharedUsers: wizardContext.sharedUsers,
          }
          await exportService.postExportCCIIOProviderValidationsSummary(exportCciioDataValidationsSummaryModel);
        } else {
          const exportCciioDataValidationsModel: ExportCCIIODataValidationsModel = {
            eMailOnSuccess: wizardContext.alertWhenReady,
            exportName: wizardContext.reportName,
            formatType: getExportType(wizardContext.reportFormat),
            providerSetVersionSelections: cciioProviderSetVersionSelections,
            runAsynchronous: true,
            separateReportOutput: wizardContext.splitReport,
            sharedUsers: wizardContext.sharedUsers,
          }
          await exportService.postExportCCIIOProviderValidations(exportCciioDataValidationsModel);
        }
        break;
      case "specialty-summary-zip-code": {
        /* oxlint-disable sort-keys */
        const zipAnalysisRequestModel: ExportBulkSpecialtySummaryByZipRequestModel = {
          allowParallelization: true,
          customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
          eMailOnSuccess: wizardContext.alertWhenReady,
          excludeZeroMembership: wizardContext.excludeZeroMembership,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          generateNoDataFoundReadme: true,
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includeFacilitySpecialties: wizardContext.includeFacilitySpecialties,
          includeInformationalSpecialties: wizardContext.includeInformationalSpecialties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includePhysicianSpecialties: wizardContext.includePhysicianSpecialties,
          includeRequiredSpecialties: wizardContext.includeRequiredSpecialties,
          publishedOnly: wizardContext.publishedOnly,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          regionSetIds: wizardContext.allServiceAreas ? [] : wizardContext.serviceAreas.flatMap(s => s.childServiceAreas.filter((sp) => sp.isChecked).map(c => c.serviceAreaValue)),
          reportCustomizationId: columnPresetId,
          runAsynchronous: true,
          separateReportOutput: wizardContext.splitReport,
          sharedUsers: wizardContext.sharedUsers,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          specialtyGroupCodes: wizardContext.allSpecialties ? [] : wizardContext.specialties.filter((sp) => sp.isChecked).map((sp) => sp.specialtyGroupCode),
        };
        /* oxlint-enable sort-keys */
        await exportService.postVolumeZipAnalysisResults(zipAnalysisRequestModel);
        break;
      }
      case "zip-class-aggregate": {
        /* oxlint-disable sort-keys */
        const zipClassAggregateRequestModel: ExportBulkZipClassAggregateResultsRequestModel = {
          allowParallelization: true,
          customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          generateNoDataFoundReadme: true,
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includeFacilitySpecialties: wizardContext.includeFacilitySpecialties,
          includeInformationalSpecialties: wizardContext.includeInformationalSpecialties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includePhysicianSpecialties: wizardContext.includePhysicianSpecialties,
          includeRequiredSpecialties: wizardContext.includeRequiredSpecialties,
          publishedOnly: wizardContext.publishedOnly,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          regionSetIds: wizardContext.allServiceAreas ? [] : wizardContext.serviceAreas.flatMap(s => s.childServiceAreas.filter((sp) => sp.isChecked).map(c => c.serviceAreaValue)),
          reportCustomizationId: columnPresetId,
          runAsynchronous: true,
          separateReportOutput: wizardContext.splitReport,
          sharedUsers: wizardContext.sharedUsers,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          specialtyGroupCodes: wizardContext.allSpecialties ? [] : wizardContext.specialties.filter((sp) => sp.isChecked).map((sp) => sp.specialtyGroupCode),
          zipClass: wizardContext.zipClassification || null,
        };
        /* oxlint-enable sort-keys */
        await exportService.postVolumeZipClassAggregateResults(zipClassAggregateRequestModel);
        break;
      }
      default:
        toast("This functionality is not available yet.",
          {
            containerId: "z-index-1030",
            type: "error",
          }
        );
    }
    setPageIsValid(false);
    toast.info('Report Submitted Successfully',
      {
        containerId: "z-index-1030",
        type: "success",
      }
    );
  };

  return (
    <Page documentTitle={`Volume ${pageTitle}`}>
      <PageInner className="vr__wizard">
        <PageHeader title={pageTitle} disableHR={true}>
          <PageHeaderRight>
            <div className="mt-3">
              {wizardContext.reportType !== "pci" && (
                <React.Fragment>
                  <RoleRestricted allowedRoles={[Roles.RESOURCE_ADMIN, Roles.PREVIEW]}>
                    <QESButton
                      qesType="secondary"
                      qesTestId="volume-reports__published_results_only"
                      onClick={() => { wizardContext.showPublishedResultsOnly(); furthestPage.current = VolumeReportsWizardContextSteps.Filters; }}
                    >
                      {wizardContext.publishedOnly ? "Include Unpublished Results" : "Published Results Only"}
                    </QESButton>
                  </RoleRestricted>
                  {hasDashboardFilters && !isNPMDashboardActive && (
                    <QESButton
                      qesType="secondary"
                      qesTestId="volume-reports__apply-dashboard-filters"
                      onClick={() => { wizardContext.applyDashboardFilters(); furthestPage.current = VolumeReportsWizardContextSteps.Filters; }}
                    >
                      Apply Project Dashboard Filters
                    </QESButton>
                  )}
                </React.Fragment>
              )}
              <Link to='/reports' className="qes-button qes-button-primary" >Reports</Link>
            </div>
          </PageHeaderRight>
        </PageHeader>
        <WizardSteps currentStep={getCurrentStep()} steps={getHumanReadablePageNames()} setStep={handleUpdatePage} />
        <hr />
        <div ref={wizardBodyRef}>
          {wizardContext.showChangeWarning && !showNoProjectsAlert && (
            <QESAlert type="warning" persist={true} classNames="mt-3">Changing anything on this page will reset later steps.</QESAlert>
          )}
          {showNoProjectsAlert && (
            <QESAlert type="danger" persist={true} classNames="mt-3">
              The applied dashboard filters did not find any projects with successful adequacy results, so no reports can be made.
            </QESAlert>
          )}
          {wizardContext.error && (
            <QESAlert type="danger" persist={true} classNames="mt-3">
              {wizardContext.error}
            </QESAlert>
          )}
          {wizardContext.loading ?
            !!wizardContext.error ?
              null :
              <LoadingSpinner /> :
            currentWizardStep}
        </div>
      </PageInner>
      <WizardNavigationBar
        leftSide={(
          <Link to="/reports" className="qes-button qes-button-secondary danger" data-testid="vr__cancel">Cancel</Link>
        )}
        rightSide={(
          <div className="volume-reports__nav-buttons">
            {
              (((!["pci", "cciio", "specialtyaggregate", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) && wizardContext.page !== VolumeReportsWizardContextSteps.SelectServiceArea)
                || (["specialtyaggregate"].includes(wizardContext.reportType) && wizardContext.page !== VolumeReportsWizardContextSteps.SelectSpecialty)) ||
                (["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) && wizardContext.page !== VolumeReportsWizardContextSteps.SelectProjects)) &&
              <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="volume-reports__previous">Previous</QESButton>}
            {wizardContext.page === VolumeReportsWizardContextSteps.Configure ? (
              <QESButton disabled={!pageIsValid} qesType="primary" qesTestId="volume-reports__generate" className="ms-3" onClick={handleGenerate}>
                Generate
              </QESButton>
            ) : (
              <QESButton onClick={validateAndAdvance}
                disabled={!pageIsValid || (wizardContext.page === VolumeReportsWizardContextSteps.Filters && !specialtySelectionValid)}
                qesTestId="volume-reports__next" className="ms-3">Next</QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );
};

export default VolumeReportsWizard;
